import React, { useState, useCallback } from "react";
import { SaveEditFormResponse, EditFormElement } from "@kaltura/mediaspace-shared-edit-entry";
import { noop, translate } from "@kaltura/mediaspace-shared-utils";
import { KalturaExternalMediaEntry } from "kaltura-typescript-client/api/types/KalturaExternalMediaEntry";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { ModalActions, ModalTitle, Button } from "@kaltura/ds-react-components";
import YouTubeEditForm from "./YouTubeEditForm";
import { StyledEditModalContent } from "./StyleComponents";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { PostSaveComponent, PreSaveComponent } from "@kaltura/mediaspace-shared-upload";

interface YouTubeEditModalProps {
    open: boolean;
    onCancel: () => void;
    youtubeEntry: KalturaExternalMediaEntry;
    onClose?: (shouldRedirect?: boolean) => void;
    fieldsStructure: EditFormElement[];
    preSaveComponent?: KmsTypeComponent;
    postSaveComponent?: KmsTypeComponent;
    publishEnabled?: boolean;
    showMediaLink?: boolean;
    showEditFormButtons?: boolean;
    formId?: string;
    onCanSaveForm?: (canSaveForm: boolean) => void;
    onReadyForPublish?: (entryIds: string[], readyForPublish: boolean) => void;
    onSubmitStarted?: () => void;
    onSubmit?: (entry: KalturaExternalMediaEntry) => void;
}

/*
 * YouTubeEditModal is a modal form component for editing metadata of a YouTube-imported entry.
 * It manages the state and logic for the edit form, and delegates the UI rendering to YouTubeEditForm.
 * The form handles save/cancel actions, shows processing state, and displays toast notifications on completion.
 *
 * Props:
 * - open: Controls modal visibility.
 * - onCancel: Callback for cancel action.
 * - youtubeEntry: The YouTube entry to edit.
 * - onClose: Callback when the modal is closed. Receives an optional shouldRedirect boolean.
 * - fieldsStructure: Structure of editable form fields.
 * - publishEnabled: Enables or disables publish functionality (default: true).
 * - showMediaLink: Whether to show the media link (default: true).
 * - showEditFormButtons: Whether to show the edit form action buttons (default: true).
 * - formId: Optional form id to use for the form element.
 * - onCanSaveForm: Callback to notify if the form can be saved.
 * - onReadyForPublish: Callback when entry is ready for publish.
 * - onSubmitStarted: Callback when form submit starts.
 * - onSubmit: Callback when form is submitted.
 */
export const YouTubeEditModal: React.FC<YouTubeEditModalProps> = ({
    youtubeEntry,
    fieldsStructure,
    onClose,
    onCanSaveForm,
    onReadyForPublish,
    onSubmitStarted,
    onSubmit,
    publishEnabled = true,
    showMediaLink = true,
    showEditFormButtons = true,
    formId: externalFormId,
    preSaveComponent,
    postSaveComponent,
}) => {
    const [editRowKey, setEditRowKey] = useState<number>(0);
    const [formProcessing, setFormProcessing] = useState<boolean>(false);
    const [canSaveForm, setCanSaveForm] = useState<boolean>(false);
    const { showToast } = useToastsContext();

    const [formData, setFormData] = useState<{ entryIds: string[]; data: any }>({ entryIds: [], data: {} });
    const [displayPreSaveComponent, setDisplayPreSaveComponent] = useState<boolean>(false);
    const [displayPostSaveComponent, setDisplayPostSaveComponent] = useState<boolean>(false);
    const [saveConfirmationResolvePromise, setSaveConfirmationResolvePromise] = useState<
        ((value: { confirm: boolean; isEditable: boolean }) => void) | null
    >(null);

    const showPreSaveComponent = useCallback(
        (entryIds: string[], values: any): Promise<{ confirm: boolean; isEditable: boolean }> => {
            return new Promise<{ confirm: boolean; isEditable: boolean }>((resolve) => {
                setFormData({ entryIds, data: values });
                setDisplayPreSaveComponent(true);
                setSaveConfirmationResolvePromise(() => resolve);
            });
        },
        []
    );

    const onBeforeSubmit = async (entryIds: string[], values: any) => {
        if (!preSaveComponent) {
            return { confirm: true, isEditable: true };
        }

        const { confirm, isEditable } = await showPreSaveComponent(entryIds, values);

        return { confirm, isEditable };
    };

    const handleOnCanSaveForm = (canSave: boolean) => {
        setCanSaveForm(canSave);
        onCanSaveForm?.(canSave);
    };

    const handleSubmitStarted = () => {
        setFormProcessing(true);
        onSubmitStarted?.();
    };

    const handleSave = ({ error, result }: SaveEditFormResponse) => {
        if (error) {
            showToast({ severity: "error", message: translate("Failed to update information") });
        }
        else {
            showToast({ severity: "success", message: translate("Your information has been successfully updated.") });

            if (postSaveComponent) {
                setDisplayPostSaveComponent(true);
            }
        }

        // In case that the we have postSaveComponent and there is no error, we will call to onClose only after the postSaveComponent is closed.
        // If there is no postSaveComponent or there is an error, we will call to onClose immediately.
        // We need it because in case onClose doing redirect to the entry page, we need to display the postSaveComponent before redirecting.
        if (!postSaveComponent || (postSaveComponent && error)) {
            onSubmit?.(youtubeEntry);
            onClose?.(); // default shouldRedirect is true
        }
    };

    const formId = externalFormId ?? "youtube-edit-form";

    return (
        <>
            <ModalTitle
                classes={{ closeButton: "youtube-import-edit-modal-close" }}
                closeButtonAriaLabel={translate("Close youtube entry edit form")}
            >
                {translate("Import from YouTube")}
            </ModalTitle>
            <StyledEditModalContent>
                <YouTubeEditForm
                    youtubeEntry={youtubeEntry}
                    fieldsStructure={fieldsStructure}
                    editRowKey={editRowKey}
                    onCancel={noop}
                    onEdit={noop}
                    onBeforeSubmit={onBeforeSubmit}
                    onSubmit={handleSave}
                    onSubmitStarted={handleSubmitStarted}
                    onRemove={noop}
                    onClose={() => {
                        setEditRowKey((prevState) => prevState + 1);
                    }}
                    publishEnabled={publishEnabled}
                    showMediaLink={showMediaLink}
                    formId={formId}
                    showEditFormButtons={false}
                    onCanSaveForm={handleOnCanSaveForm}
                    onReadyForPublish={onReadyForPublish}
                    singleUpload={true}
                />
            </StyledEditModalContent>

            {showEditFormButtons && (
                <ModalActions>
                    <Button
                        kuid={"edit-form-cancel"}
                        form={formId}
                        variant={"borderless"}
                        color={"secondary"}
                        onClick={() => onClose?.(false)}
                    >
                        {translate("Close")}
                    </Button>
                    <Button
                        kuid={"edit-form-save"}
                        form={formId}
                        variant={"pill"}
                        loading={formProcessing}
                        type={"submit"}
                        disabled={!canSaveForm}
                    >
                        {translate("Save & Close")}
                    </Button>
                </ModalActions>
            )}

            {preSaveComponent && (
                <PreSaveComponent
                    kmsComponent={preSaveComponent}
                    display={displayPreSaveComponent}
                    formData={formData}
                    saveConfirmationResolvePromise={saveConfirmationResolvePromise}
                    setDisplayPreSaveComponent={setDisplayPreSaveComponent}
                />
            )}
            {postSaveComponent && (
                <PostSaveComponent
                    kmsComponent={postSaveComponent}
                    display={displayPostSaveComponent}
                    formData={formData}
                    onClose={() => {
                        setDisplayPostSaveComponent(false);
                        onSubmit?.(youtubeEntry);
                        onClose?.(); // default shouldRedirect is true
                    }}
                />
            )}
        </>
    );
};

export default YouTubeEditModal;
